/**
 * Achats intégrés — RevenueCat (même architecture que GameTime/purchasesManager.js).
 * Mobile uniquement : sur le web, l'abonnement se prend depuis l'app (message dans Premium.tsx).
 * Le statut est synchronisé dans users/$uid/premium ; le serveur relit ce nœud pour
 * appliquer les quotas (requirePremium / quotas IA).
 */
import { Purchases } from "@revenuecat/purchases-capacitor";
import { Capacitor } from "@capacitor/core";
import { ref, get, set } from "firebase/database";
import type { User } from "firebase/auth";
import { db } from "../firebase";
import { track } from "./analytics";

const API_KEYS = {
  android: process.env.REACT_APP_REVENUECAT_ANDROID_KEY || "",
  ios: process.env.REACT_APP_REVENUECAT_IOS_KEY || "",
};

export const ENTITLEMENT_ID = "Scholeo Premium";
export const PACKAGE_IDS = { MONTHLY: "monthly", YEARLY: "yearly", FAMILY: "family" } as const;

let isInitialized = false;

export function purchasesAvailable(): boolean {
  const platform = Capacitor.getPlatform();
  return platform !== "web" && Boolean(platform === "android" ? API_KEYS.android : API_KEYS.ios);
}

export async function initializePurchases(user: User): Promise<void> {
  if (isInitialized || !purchasesAvailable()) return;
  try {
    const platform = Capacitor.getPlatform();
    await Purchases.configure({
      apiKey: platform === "android" ? API_KEYS.android : API_KEYS.ios,
      appUserID: user.uid,
    });
    isInitialized = true;
    await Purchases.addCustomerInfoUpdateListener(() => {
      syncPremiumStatus(user).catch(() => undefined);
    });
    await syncPremiumStatus(user);
  } catch (error) {
    console.error("[purchases] init error", error);
  }
}

export async function getOfferings() {
  try {
    const offerings = await Purchases.getOfferings();
    return offerings.current || null;
  } catch (e) {
    return null;
  }
}

export async function purchasePackage(packageId: string, user: User): Promise<boolean> {
  const offering = await getOfferings();
  if (!offering) throw new Error("no_offerings");
  const pkg = offering.availablePackages.find((p) => p.identifier === packageId);
  if (!pkg) throw new Error("package_not_found");
  try {
    await Purchases.purchasePackage({ aPackage: pkg });
    await syncPremiumStatus(user);
    return true;
  } catch (error: any) {
    if (error?.userCancelled) return false;
    throw error;
  }
}

export async function restorePurchases(user: User): Promise<boolean> {
  const { customerInfo } = await Purchases.restorePurchases();
  await syncPremiumStatus(user);
  return Object.keys(customerInfo.entitlements.active).length > 0;
}

export async function checkPremiumStatus() {
  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    const ent = customerInfo.entitlements.active[ENTITLEMENT_ID];
    return { isPremium: Boolean(ent), entitlement: ent || null };
  } catch (e) {
    return { isPremium: false, entitlement: null };
  }
}

/** Écrit users/$uid/premium ; ne touche jamais un compte marqué isAdmin. */
export async function syncPremiumStatus(user: User): Promise<void> {
  const premiumRef = ref(db, `users/${user.uid}/premium`);
  const existing = (await get(premiumRef)).val();
  if (existing && existing.isAdmin === true) return;
  const status = await checkPremiumStatus();
  if (status.isPremium && status.entitlement) {
    const e = status.entitlement;
    await set(premiumRef, {
      active: true,
      type: "iap",
      store: e.store,
      productId: e.productIdentifier,
      purchaseDate: e.latestPurchaseDate,
      expirationDate: e.expirationDate || null,
      willRenew: e.willRenew,
      periodType: e.periodType,
      lastSyncDate: Date.now(),
    });
    if (!(existing && existing.active === true)) track("subscribe_completed", { plan: e.productIdentifier });
  } else {
    await set(premiumRef, { active: false, type: null, lastSyncDate: Date.now() });
  }
}

export async function openSubscriptionManagement(): Promise<void> {
  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    const url =
      customerInfo.managementURL ||
      (Capacitor.getPlatform() === "ios" ? "https://apps.apple.com/account/subscriptions" : "https://play.google.com/store/account/subscriptions");
    window.open(url, "_system");
  } catch (e) {
    /* silencieux */
  }
}
